/**
 * Adapter pour balance BAXTRAN SL
 * Utilise Web Serial API pour communication série
 *
 * Configuration série BAXTRAN SL :
 * - Baud rate: 9600
 * - Data bits: 8
 * - Parity: None
 * - Stop bits: 1
 * - Flow control: None
 *
 * Format de trame reçue :
 * "ST,GS,+00123.4kg\r\n"
 * ST = Stable, US = Unstable
 * GS = Gross weight, NT = Net weight
 */

import { ScaleAdapter, WeightReading, ScaleError } from './ScaleAdapter';
import { WEB_SERIAL_CONFIG, USB_FILTERS } from './scale.config';
import { logger } from '../../utils/logger';

export class BaxtranSLAdapter implements ScaleAdapter {
  readonly modelName = 'BAXTRAN SL';
  readonly supportsAutoConnect = true;

  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private subscribers: Array<(reading: WeightReading) => void> = [];
  private readLoopActive = false;
  private decoder = new TextDecoder();
  private buffer = '';

  /**
   * Connecter à la balance BAXTRAN SL via Web Serial API
   */
  async connect(): Promise<void> {
    try {
      // Vérifier si Web Serial API est disponible
      if (!('serial' in navigator)) {
        throw new ScaleError(
          'Web Serial API non supportée par ce navigateur',
          'NOT_SUPPORTED'
        );
      }

      // Demander à l'utilisateur de sélectionner le port série
      // Utiliser les filtres USB pour détecter automatiquement les adaptateurs USB-RS232
      this.port = await navigator.serial.requestPort({
        filters: USB_FILTERS,
      });

      // Ouvrir le port avec la configuration BAXTRAN SL testée et validée
      // 9600 bauds, 8N1 (8 data bits, No parity, 1 stop bit)
      await this.port.open(WEB_SERIAL_CONFIG);

      // Obtenir les streams de lecture/écriture
      this.reader = this.port.readable?.getReader() ?? null;
      this.writer = this.port.writable?.getWriter() ?? null;

      if (!this.reader) {
        throw new ScaleError(
          'Impossible d\'obtenir le reader du port série',
          'CONNECTION_ERROR'
        );
      }

      // Démarrer la boucle de lecture
      this.startReadLoop();

      logger.log('[BaxtranSLAdapter] Connecté avec succès');
    } catch (error) {
      if (error instanceof ScaleError) throw error;
      throw new ScaleError(
        'Erreur de connexion à la balance',
        'CONNECTION_ERROR',
        error as Error
      );
    }
  }

  /**
   * Déconnecter la balance
   */
  async disconnect(): Promise<void> {
    this.readLoopActive = false;

    if (this.reader) {
      await this.reader.cancel();
      this.reader.releaseLock();
      this.reader = null;
    }

    if (this.writer) {
      this.writer.releaseLock();
      this.writer = null;
    }

    if (this.port) {
      await this.port.close();
      this.port = null;
    }

    this.buffer = '';
    logger.log('[BaxtranSLAdapter] Déconnecté');
  }

  /**
   * Vérifier si la balance est connectée
   */
  isConnected(): boolean {
    return this.port !== null && this.reader !== null;
  }

  /**
   * Lire le poids actuel (retourne la dernière valeur reçue)
   */
  async readWeight(): Promise<WeightReading> {
    if (!this.isConnected()) {
      throw new ScaleError('Balance non connectée', 'NOT_CONNECTED');
    }

    // Pour BAXTRAN SL, on attend la prochaine lecture via la boucle
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        unsubscribe();
        reject(new ScaleError('Timeout lors de la lecture du poids', 'READ_ERROR'));
      }, 5000);

      const unsubscribe = this.subscribe((reading) => {
        clearTimeout(timeout);
        unsubscribe();
        resolve(reading);
      });
    });
  }

  /**
   * Tarrer la balance (envoyer commande T)
   */
  async tare(): Promise<void> {
    if (!this.isConnected() || !this.writer) {
      throw new ScaleError('Balance non connectée', 'NOT_CONNECTED');
    }

    try {
      const command = new TextEncoder().encode('T\r\n');
      await this.writer.write(command);
      logger.log('[BaxtranSLAdapter] Commande TARE envoyée');
    } catch (error) {
      throw new ScaleError(
        'Erreur lors de l\'envoi de la commande TARE',
        'READ_ERROR',
        error as Error
      );
    }
  }

  /**
   * S'abonner aux changements de poids
   */
  subscribe(callback: (reading: WeightReading) => void): () => void {
    this.subscribers.push(callback);

    // Retourner fonction de désabonnement
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  /**
   * Boucle de lecture des données série
   */
  private async startReadLoop(): Promise<void> {
    if (!this.reader) return;

    this.readLoopActive = true;

    try {
      while (this.readLoopActive && this.reader) {
        const { value, done } = await this.reader.read();

        if (done) {
          logger.log('[BaxtranSLAdapter] Lecture terminée');
          break;
        }

        if (value) {
          this.processData(value);
        }
      }
    } catch (error) {
      logger.error('[BaxtranSLAdapter] Erreur lecture:', error);
      // Ne pas throw, juste logger
    }
  }

  /**
   * Traiter les données reçues et parser les trames
   */
  private processData(data: Uint8Array): void {
    // Ajouter au buffer
    this.buffer += this.decoder.decode(data, { stream: true });

    // Chercher les trames complètes (terminées par \r\n)
    let newlineIndex: number;
    while ((newlineIndex = this.buffer.indexOf('\r\n')) >= 0) {
      const line = this.buffer.substring(0, newlineIndex);
      this.buffer = this.buffer.substring(newlineIndex + 2);

      // Parser la trame
      const reading = this.parseLine(line);
      if (reading) {
        // Notifier tous les abonnés
        this.subscribers.forEach((callback) => callback(reading));
      }
    }
  }

  /**
   * Parser une ligne de données BAXTRAN SL
   * Supporte deux formats:
   * - Format WGT: "WGT:1 0.142P 0.000" (format moderne)
   * - Format CSV: "ST,GS,+00123.4kg" (format ancien)
   */
  private parseLine(line: string): WeightReading | null {
    try {
      // Nettoyer la ligne
      const cleanLine = line.trim();

      // Format WGT: "WGT:1 0.142P 0.000" ou "WGT:6-0.178P 0.178" (avec tare)
      // Structure: WGT:ID [ESPACE?] POIDS[STATUT] TARE
      // Note: Quand une tare est active, l'espace peut manquer et le poids peut être négatif
      const wgtMatch = cleanLine.match(/WGT:(\d+)\s*([+-]?[\d.]+)([A-Z]?)\s+([\d.]+)/);
      if (wgtMatch) {
        const [, mode, weightStr, status, tareStr] = wgtMatch;

        const weightKg = parseFloat(weightStr);
        const weightGrams = weightKg * 1000; // Convertir kg en grammes
        const tareGrams = parseFloat(tareStr) * 1000;

        // P = Poids stable (Position fixe)
        // U ou autre = Poids instable (Unstable)
        const stable = status === 'P';

        logger.log(
          `[BaxtranSLAdapter] WGT format: mode=${mode}, weight=${weightGrams}g, stable=${stable}, status=${status}, tare=${tareGrams}g`,
        );

        return {
          weight: weightGrams,
          stable,
          unit: 'g',
          timestamp: Date.now(),
        };
      }

      // Format CSV ancien: "ST,GS,+00123.4kg"
      const parts = cleanLine.split(',');
      if (parts.length === 3) {
        const [stabilityPart, , weightPart] = parts;

        // Extraire stabilité
        const stable = stabilityPart.trim() === 'ST';

        // Extraire poids et unité
        const csvMatch = weightPart.match(/([+-]?\d+\.?\d*)(kg|g)/i);
        if (csvMatch) {
          const [, valueStr, unitStr] = csvMatch;
          let weight = parseFloat(valueStr);
          const unit = unitStr.toLowerCase() as 'kg' | 'g';

          // Convertir en grammes
          if (unit === 'kg') {
            weight = weight * 1000;
          }

          logger.log(
            `[BaxtranSLAdapter] CSV format: weight=${weight}g, stable=${stable}`,
          );

          return {
            weight,
            stable,
            unit: 'g',
            timestamp: Date.now(),
          };
        }
      }

      // Aucun format reconnu
      return null;
    } catch (error) {
      logger.warn('[BaxtranSLAdapter] Erreur parsing:', line, error);
      return null;
    }
  }
}
